import { type PowerSyncDrizzleDB, user } from "@custodia/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Why ALL the mutations below carry `.where(eq(user.id, userId))` ─────────
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * that is, in guest mode the UPDATE came out **without `WHERE`** → it rewrote ALL
 * the `user` rows of the local database. The branch existed by symmetry with
 * `getUserThemeSettingsQuery`, which for the guest reads with `.limit(1)` without a
 * filter; but a read `limit(1)` and an UPDATE without a filter are not the same
 * bet: the first picks the wrong row, the second crushes them all.
 *
 * The premise behind it ("the guest database has only one
 * `user` row") is false in a specific window: during the guest→real upgrade, the
 * guest database CONNECTS to the real account's connector and
 * auto-subscribes to streams pulling down foreign `user` rows. `user` is not
 * `localOnly`, so each touched row also enqueues its own upload op.
 *
 * The correct filter is NOT guessed: `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds the guest row with
 * `db.insert(user).values({ id: uid, … })`, where `uid = useSession().userId()`;
 * and for a guest tab `userId()` returns exactly `VISITOR_ID`
 * (`apps/web/src/lib/auth.ts`, `resolveActiveUserId`). Thus
 * `eq(user.id, userId)` points to the SAME row that the unfiltered branch intended
 * to touch when the table only had one row — identical behavior in the normal case,
 * bounded in all others. It is also the pattern already used by
 * `updateUserFontFamilyMutation` and `updateUserImageMutation`, which never had
 * a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * ── Every `UPDATE` in this file goes through `patchRowVerified` ─────────────
 * Why `user` is NOT excluded here (unlike other portfolio repos,
 * e.g. `comandero-app`) even though the seed guard in `ThemeProvider` (2026-08-13)
 * no longer competes with the server row: see the docblock at the top of
 * `../powersync/verified-write.ts`. It is not repeated here so we do not keep
 * two explanations that can age independently.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme) and not through REST: this way
 * the new photo is visible immediately and survives a cold reload without
 * a connection. The BINARY does go through REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; what travels here is only the key returned by that upload.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * The visitor has no own synchronizable row and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
